import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';

interface LazyImageProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  placeholder?: string;
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  borderRadius?: string;
  onLoad?: () => void;
  onError?: () => void;
}

const ImageContainer = styled.div<{ 
  width?: string | number; 
  height?: string | number;
  borderRadius?: string;
}>`
  position: relative;
  overflow: hidden;
  width: ${props => typeof props.width === 'number' ? `${props.width}px` : props.width || '100%'};
  height: ${props => typeof props.height === 'number' ? `${props.height}px` : props.height || 'auto'};
  border-radius: ${props => props.borderRadius || '0'};
  background: linear-gradient(45deg, rgba(58, 174, 171, 0.12), rgba(1, 74, 144, 0.10));
`;

const Image = styled.img<{ objectFit?: string }>`
  width: 100%;
  height: 100%;
  object-fit: ${props => props.objectFit || 'cover'};
  transition: opacity 0.3s ease;
  opacity: 0;
  
  &.loaded {
    opacity: 1;
  }
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(45deg, rgba(58, 174, 171, 0.12), rgba(1, 58, 112, 0.22));
  color: var(--primary-turquoise);
  font-size: 2rem;
  transition: opacity 0.3s ease;
  
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
  
  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(58, 174, 171, 0.3);
    border-top: 3px solid var(--primary-turquoise);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorPlaceholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(58, 174, 171, 0.12);
  color: var(--light-turquoise);
  text-align: center;
  padding: 20px;
  
  i {
    font-size: 3rem;
    margin-bottom: 10px;
    color: rgba(227, 227, 221, 0.35);
  }
  
  span {
    font-size: 0.9rem;
    opacity: 0.7;
  }
`;

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  fallbackSrc,
  placeholder,
  width,
  height,
  className,
  style,
  objectFit,
  borderRadius,
  onLoad,
  onError
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [triedFallback, setTriedFallback] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCurrentSrc(src);
    setIsLoaded(false);
    setHasError(false);
    setTriedFallback(false);
  }, [src]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isLoaded && imageRef.current) {
      // Animate image entrance
      gsap.fromTo(imageRef.current, 
        { opacity: 0, scale: 1.1 },
        { opacity: 1, scale: 1, duration: 0.6, ease: 'power2.out' }
      );
    }
  }, [isLoaded]);

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    if (fallbackSrc && !triedFallback) {
      setTriedFallback(true);
      setIsLoaded(false);
      setHasError(false);
      setCurrentSrc(fallbackSrc);
      return;
    }

    setHasError(true);
    if (onError) onError();
  };

  return (
    <ImageContainer
      ref={containerRef}
      width={width}
      height={height}
      borderRadius={borderRadius}
      className={className}
      style={style}
    >
      {isInView && !hasError && (
        <Image
          ref={imageRef}
          src={currentSrc}
          alt={alt}
          objectFit={objectFit}
          className={isLoaded ? 'loaded' : ''}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
      
      {!isLoaded && !hasError && (
        <Placeholder className={isLoaded ? 'hidden' : ''}>
          {placeholder ? (
            <i className={placeholder}></i>
          ) : (
            <div className="loading-spinner"></div>
          )}
        </Placeholder>
      )}
      
      {hasError && (
        <ErrorPlaceholder>
          <i className="fas fa-image"></i>
          <span>Imagen no disponible</span>
        </ErrorPlaceholder>
      )}
    </ImageContainer>
  );
};

export default LazyImage;